import { type TaxDetail, TAXES, FEES } from '../types'
import { isKnownTaxType, getTotalTaxAmount } from '../utils'

// Helper function to determine if a tax detail is a tax or fee
export const isTaxCategory = (taxDetail: TaxDetail): boolean => {
  if (!isKnownTaxType(taxDetail.name)) {
    return taxDetail.name.toLowerCase().includes('tax')
  }
  return TAXES.includes(taxDetail.name)
}

// Helper function to determine if a tax detail is a fee
export const isFeeCategory = (taxDetail: TaxDetail): boolean => {
  if (!isKnownTaxType(taxDetail.name)) {
    const name = taxDetail.name.toLowerCase()
    return name.includes('fee') || name.includes('rate') || name.includes('charge')
  }
  return FEES.includes(taxDetail.name)
}

// Filter tax details to get only taxes
export const filterTaxes = (taxDetails: TaxDetail[]): TaxDetail[] => {
  return taxDetails.filter(isTaxCategory)
}

// Filter tax details to get only fees
export const filterFees = (taxDetails: TaxDetail[]): TaxDetail[] => {
  return taxDetails.filter(isFeeCategory)
}

// Get totals for taxes by payment timing
export const getTaxesTotalsByPayment = (taxDetails: TaxDetail[]) => {
  const taxes = filterTaxes(taxDetails)
  const included = taxes.filter(tax => tax.included)
  const notIncluded = taxes.filter(tax => !tax.included)

  return {
    total: getTotalTaxAmount(taxes),
    payNow: getTotalTaxAmount(included),
    payLater: getTotalTaxAmount(notIncluded),
    currency: taxes[0]?.client?.currency ?? taxes[0]?.currency,
  }
}

// Get totals for fees by payment timing
export const getFeesTotalsByPayment = (taxDetails: TaxDetail[]) => {
  const fees = filterFees(taxDetails)
  const included = fees.filter(fee => fee.included)
  const notIncluded = fees.filter(fee => !fee.included)

  return {
    total: getTotalTaxAmount(fees),
    payNow: getTotalTaxAmount(included),
    payLater: getTotalTaxAmount(notIncluded),
    currency: fees[0]?.client?.currency ?? fees[0]?.currency,
  }
}

// Get the effective amount for a tax detail (client amount if available, otherwise regular amount)
export const getTaxEffectiveAmount = (taxDetail: TaxDetail): number => {
  return taxDetail.client?.amount ?? taxDetail.amount
}

// Get the effective currency for a tax detail (client currency if available, otherwise regular currency)
export const getTaxEffectiveCurrency = (taxDetail: TaxDetail): string => {
  return taxDetail.client?.currency ?? taxDetail.currency
}
